<template>
    <FullScreenLoader v-if="isGenerationPreview && !loaded" />
    <div v-else-if="loaded">
        <ReviewMode v-if="review && pageType !== 'coming_soon'">
            <component
                :is="currentComponent"
                :key="$route.params.projectShortId || pageType"
                v-bind="currentProps"
            />
        </ReviewMode>
        <template v-else>
            <component
                :is="currentComponent"
                :key="$route.params.projectShortId || pageType"
                v-bind="currentProps"
            />
        </template>
    </div>
    <Cursor />
</template>

<script>
    import Project from "./Project.vue"
    import ApiHelper from "../../api/ApiHelper.js";
    import { checkFonts } from "../../settingsEditor/_helpers/fontLoaders.js";
    import CustomPage from "./Page.vue";
    import PasswordProtection from "./common/PasswordProtection.vue";
    import ErrorPage from "../common/ErrorPage.vue";
    import GoogleFontsHelper from "../../common/GoogleFontsHelper.js";
    import ReviewMode from "../../view/review/ReviewMode.vue";
    import ComingSoon from "../../common/ComingSoon.vue";
    import {isOwnerPreview} from "./ownerPreview.js";
    import Cursor from "../../common/sideBars/design/Cursor.vue";
    import FullScreenLoader from "../../common/FullScreenLoader.vue";
    import {mapGetters} from "vuex";
    import {UserActions} from "@/scripts/store/actions/UserActions.js";
    import {PortfolioActions} from "@/scripts/store/actions/PortfolioActions.js";
    import {store} from "@/scripts/store/FolioStore.js";
    import {
        findGeneratedPreviewPage,
        isGeneratedProjectTemplatePreview,
        isTemplatePreviewRoute,
        readGeneratedPortfolioPreview
    } from "@/scripts/adminSite/aiPortfolioGenerator/portfolioPreviewCache.js";
    import {getPortfolioGenerationPreview} from "@/scripts/api/PortfolioGenerationAPI.js";
    import {
        findPortfolioGenerationPreviewPage,
        isPortfolioGenerationPreviewRoute
    } from "@/scripts/view/portfolio/generationPreview.js";

    export default {
        name: "PageWrapper",
        props: ['review'],
        components: {
            ReviewMode,
            ErrorPage,
            PasswordProtection,
            CustomPage,
            Project,
            ComingSoon,
            Cursor,
            FullScreenLoader
        },
        data: () => ({
            pageType: null,
            loaded: false
        }),
        created() {
            this._unwatch = this.$watch(
                () => this.$route.params,
                () => {
                    this.loaded = false;
                    this.loadDataIfNeeded(this.$route);
                },
                { immediate: true }
            );
        },
        beforeUnmount() {
            this._unwatch?.();
        },
        methods: {
            async loadDataIfNeeded(to) {
                if (isPortfolioGenerationPreviewRoute(to)) {
                    await this.loadPortfolioGenerationPreviewPage(to);
                    return;
                }

                if (this.hydrateFromGeneratedPreviewCache(to)) {
                    return;
                }

                if (this.hydrateFromExistingViewData(to) || this.hydrateFromEditorStoreIfMatch(to)) {
                    return;
                }

                try {
                    const url = this.fetchUrl(to);
                    const res = await ApiHelper.get(url);
                    this.setData(null, res.data);
                } catch (err) {
                    this.setData(err, null);
                }
            },
            async loadPortfolioGenerationPreviewPage(to) {
                const generationId = to.params.generationId;
                if (this.hydrateFromExistingGenerationPreview(generationId, to.params.projectShortId)) {
                    return;
                }

                try {
                    const res = await getPortfolioGenerationPreview(generationId);
                    const payload = res.data;
                    const page = findPortfolioGenerationPreviewPage(
                        payload,
                        to.params.projectShortId
                    );
                    if (!page) {
                        throw new Error("Portfolio preview page does not exist.");
                    }

                    this.setData(null, {
                        ...payload,
                        project: page,
                        pageType: this.getPageTypeByProjectType(page)
                    }, generationId);
                } catch (error) {
                    this.setPreviewError();
                }
            },
            setPreviewError() {
                this.pageType = "not_found";
                this.loaded = true;
            },
            hydrateFromExistingGenerationPreview(generationId, projectShortId) {
                if (this.viewData.generationPreviewId !== generationId) {
                    return false;
                }

                const page = findPortfolioGenerationPreviewPage(this.viewData, projectShortId);
                if (!page) {
                    return false;
                }

                this.$store.commit("view/updateView", {
                    ...this.viewData,
                    project: page,
                    activePage: page.shortId,
                    generationPreviewId: generationId,
                });
                this.$store.commit("view/updateActivePage", page.shortId);
                this.$store.state.project = page;
                this.pageType = this.getPageTypeByProjectType(page);
                this.loaded = true;
                store.commit("view/updateViewModeReadiness", true);
                return true;
            },
            setData(err, data, generationPreviewId = null) {
                if(err && !data) {
                    if(err.response.status === 404) {
                        this.pageType = 'not_found'
                        this.loaded = true;
                        return;
                    } else if (err.response.status === 401) {
                        this.pageType = 'coming_soon';
                        this.loaded = true;
                        return;
                    }
                    this.applyViewData(this.buildViewDataFromResponse(err.response?.data));
                    this.pageType = 'password';
                    this.loaded = true;
                    return;
                }

                this.applyViewData(this.buildViewDataFromResponse(data, generationPreviewId));
                this.pageType = data.pageType;
                this.loaded = true;
            },
            fetchUrl(to) {
                let url = '/data/project'
                let projectShortId = to.params.projectShortId;
                let portfolioShortId = to.params.portfolioShortId;

                if (to.matched?.some(route => route.meta?.adminPreview) && portfolioShortId && projectShortId) {
                    return '/api/admin/case-study-preview/'
                        + encodeURIComponent(portfolioShortId)
                        + '/'
                        + encodeURIComponent(projectShortId);
                }

                if (isOwnerPreview(to) && portfolioShortId && projectShortId) {
                    return '/api/case-study-preview/'
                        + encodeURIComponent(portfolioShortId)
                        + '/'
                        + encodeURIComponent(projectShortId);
                }

                if(portfolioShortId) {
                    url += "/" + portfolioShortId;
                }

                if(projectShortId) {
                    url += "/" + projectShortId;
                }

                return url;
            },
            getPageTypeByProjectType(project){
                switch (project.type) {
                    case 'caseStudy': return 'project';
                    default: return 'custom'
                }
            },
            hydrateFromGeneratedPreviewCache(to) {
                if (!isGeneratedProjectTemplatePreview(to)) {
                    return false;
                }

                const payload = readGeneratedPortfolioPreview(to.params.portfolioShortId);
                const page = findGeneratedPreviewPage(payload, to.params.projectShortId);
                if (!page) {
                    return false;
                }

                try {
                    this.setData(null, {
                        ...payload,
                        project: page,
                        pageType: this.getPageTypeByProjectType(page)
                    });
                    return true;
                } catch (error) {
                    return false;
                }
            },
            hydrateFromExistingViewData(to) {
                const projectShortId = to.params.projectShortId;
                const portfolioShortId = to.params.portfolioShortId;
                const existingPortfolio = this.viewData.portfolio;
                const existingPages = [
                    ...(this.viewData.portfolioPages || []),
                    ...(this.viewData.caseStudies || [])
                ];

                if (
                    !existingPortfolio ||
                    !existingPages?.length ||
                    !(existingPortfolio.shortId === portfolioShortId || this.rootIsOwnDomain)
                ) {
                    return false;
                }

                const page = existingPages.find(p => p.shortId === projectShortId);
                if (!page) {
                    return false;
                }

                if (this.requiresPassword(page)) {
                    return false;
                }

                this.$store.commit('view/updateViewProject', page);
                this.$store.commit('view/updateActivePage', page.shortId);
                this.$store.state.project = page;
                this.$store.commit("view/updateView", { generationPreviewId: null });
                this.pageType = this.getPageTypeByProjectType(page);
                this.loaded = true;
                store.commit("view/updateViewModeReadiness", true);
                return true;
            },
            hydrateFromEditorStoreIfMatch(to) {
                const editorPortfolio = this.$store.state.portfolio;
                const editorPages = [
                    ...(this.$store.state.portfolioPages || []),
                    ...(this.$store.state.caseStudyPages || [])
                ];

                if (!editorPortfolio || !editorPages.length) {
                    return false;
                }

                const projectShortId = to.params.projectShortId;
                const portfolioShortId = to.params.portfolioShortId;

                const matchesPortfolio =
                    editorPortfolio.shortId === portfolioShortId ||
                    editorPortfolio._id === portfolioShortId ||
                    editorPortfolio.id === portfolioShortId;

                if (!matchesPortfolio) {
                    return false;
                }

               const page = editorPages.find(p => p.shortId === projectShortId);
                if (!page) {
                    return false;
                }

                this.$store.commit('view/updateView', {
                    portfolio: editorPortfolio,
                    project: page,
                    user: this.$store.state.user,
                    portfolioPages: this.$store.state.portfolioPages.filter(page => !page.deletedAt),
                    caseStudies: this.$store.state.caseStudyPages,
                    activePage: page.shortId,
                    loggedInUserId: this.$store.state.user?._id,
                    portfolioTemplate: editorPortfolio.templateSettings,
                    generationPreviewId: null
                });
                this.$store.commit('view/updateActivePage', page.shortId);
                this.$store.state.project = page;
                this.pageType = this.getPageTypeByProjectType(page);
                this.loaded = true;
                store.commit("view/updateViewModeReadiness", true);
                return true;
            },
            requiresPassword(page) {
                const password = this.$cookies.get(page.shortId);
                const portfolio = this.viewData.portfolio || this.$store.state.portfolio;
                const hasPassword = page.password || portfolio?.password;
                const isValidPassword =
                    (page.password && page.password === password) ||
                    (portfolio?.password && portfolio.password === password);
                return hasPassword && !this.isOwnerIsLoggedIn && !isValidPassword;
            },
            buildViewDataFromResponse(data, generationPreviewId = null) {
                if(!this.viewData.fontsLoaded || data.pageType === 'project') {
                    checkFonts(data.fonts);
                    data.fontNames
                        .filter((fontName) => !data.fonts.map(x => x.name).includes(fontName))
                        .forEach((name) => GoogleFontsHelper.getFontURL(name));
                    this.viewData.fontsLoaded = true;
                }

                if (data.loggedInUserId === data.project.ownerId && !isTemplatePreviewRoute(this.$route)) {
                    UserActions.getUser()
                        .then(() => PortfolioActions.getPortfolios())
                        .then(() => store.commit("view/updateViewModeReadiness", true))
                        .catch(() => store.commit("view/updateViewModePreloadFailed", true));
                }

                this.$store.state.portfolio = data.portfolio;
                this.$store.state.project = data.project;

                if(!data.portfolio.templateSettings.theme.includes('profile_')) {
                    try {
                        import(`../../../styles/_${data.portfolio.templateSettings.theme.replaceAll('_', '-').replace('profile_', '')}-base.scss`);
                    }
                    catch (e) {
                        console.log('failed to import scss')
                    }
                }

                return {
                    portfolio:data.portfolio,
                    project:data.project,
                    user:data.user,
                    portfolioPages:data.portfolioPages,
                    caseStudies:data.caseStudies,
                    activePage:data.project.shortId,
                    loggedInUserId:data.loggedInUserId,
                    portfolioTemplate: data.portfolioTemplate,
                    generationPreviewId
                };
            },
            applyViewData(viewData) {
                this.$store.commit('view/updateView', viewData);
                this.$store.commit('view/updateActivePage', viewData.activePage);
            }
        },
        computed: {
            ...mapGetters("view", ["viewData"]),
            currentComponent() {
                if (!this.loaded) return null;
                switch (this.pageType) {
                    case 'project': return 'Project';
                    case 'custom': return 'CustomPage';
                    case 'password': return 'PasswordProtection';
                    case 'not_found': return 'ErrorPage';
                    case 'coming_soon': return 'ComingSoon';
                    default: return null;
                }
            },
            currentProps() {
                switch (this.pageType) {
                    case 'password':
                        return { fetchUrl: this.fetchUrl, setData: this.setData, type: 'Case Study' };
                    case 'coming_soon':
                        return { type: 'Case Study' };
                    case 'not_found':
                        return { error: { code: 404, title: "This page doesn't exist", message: "We can't seem to find the page you are looking for" } };
                    default:
                        return {};
                }
            },
            isOwnerIsLoggedIn() {
                return this.viewData.loggedInUserId === this.$store.state.project.ownerId
            },
            rootIsOwnDomain() {
                return this.viewData.portfolio.ownDomainAddress && location.origin.includes(this.viewData.portfolio.ownDomainAddress);
            },
            isGenerationPreview() {
                return isPortfolioGenerationPreviewRoute(this.$route);
            },
        }
    }
</script>

<style lang="scss">
    .ufo--page-sections,
    .footer-info {
        --black: #140a2f;
        --white: #FFFFFF;
        --red: #fc3d83;
        --blue: #4a3dff;
        --purple: #5c50ff;
    }
</style>
